import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useWorkout } from './WorkoutContext';
import { useServiceUnavailable } from '../hooks/useServiceUnavailable';
import { BodyWeightService } from '../services/BodyWeightService';
import { getDateKey } from '../utils';

interface BodyWeightContextType {
  bodyWeight: string;
  setBodyWeight: (value: string) => void;
  fatPercentage: string;
  setFatPercentage: (value: string) => void;
  waistCircumference: string;
  setWaistCircumference: (value: string) => void;
  previousBodyWeight: number | null;
  bodyWeightDays: Set<string>;
  bodyWeightLoading: boolean;
  bodyWeightError: string | null;
  handleLogBodyWeight: (data: { weight: string; fatPercentage: string; waistCircumference: string }) => Promise<boolean>;
  refreshBodyWeightForDate: (date: Date) => Promise<void>;
}

const BodyWeightContext = createContext<BodyWeightContextType | undefined>(undefined);

export const BodyWeightProvider: React.FC<{
  children: React.ReactNode;
  onServiceUnavailable: () => void;
}> = ({ children, onServiceUnavailable }) => {
  const { idToken } = useAuth();
  const { selectedDate, calendarMonth } = useWorkout();
  const { handleError } = useServiceUnavailable(onServiceUnavailable);

  const [bodyWeight, setBodyWeight] = useState('');
  const [fatPercentage, setFatPercentage] = useState('');
  const [waistCircumference, setWaistCircumference] = useState('');
  const [previousBodyWeight, setPreviousBodyWeight] = useState<number | null>(null);
  const [bodyWeightDays, setBodyWeightDays] = useState<Set<string>>(new Set());
  const [bodyWeightLoading, setBodyWeightLoading] = useState(false);
  const [bodyWeightError, setBodyWeightError] = useState<string | null>(null);

  // Load bodyweight for selected date
  useEffect(() => {
    if (!selectedDate || !idToken) {
      setBodyWeight('');
      setFatPercentage('');
      setWaistCircumference('');
      setPreviousBodyWeight(null);
      setBodyWeightLoading(false);
      return;
    }
    const dateKey = getDateKey(selectedDate);

    const loadBodyWeightForDate = async () => {
      setBodyWeightLoading(true);
      setBodyWeightError(null);
      try {
        const [data, prev] = await Promise.all([
          BodyWeightService.fetchBodyWeightForDate(dateKey),
          BodyWeightService.fetchPreviousBodyWeight(dateKey),
        ]);
        setBodyWeight(data.weight);
        setFatPercentage(data.fatPercentage);
        setWaistCircumference(data.waistCircumference);
        setPreviousBodyWeight(prev);
      } catch (error: any) {
        console.error(`Failed to fetch bodyweight for date ${dateKey}:`, error);
        setBodyWeightError('Failed to fetch body weight');
        setBodyWeight('');
        setFatPercentage('');
        setWaistCircumference('');
        setPreviousBodyWeight(null);
        handleError(error);
      } finally {
        setBodyWeightLoading(false);
      }
    };
    loadBodyWeightForDate();
  }, [selectedDate, idToken, handleError]);

  // Fetch bodyweight days for calendar month
  useEffect(() => {
    if (!idToken || !calendarMonth) return;
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth() + 1;

    BodyWeightService.fetchBodyWeightDates(year, month)
      .then((dates) => setBodyWeightDays(new Set(dates)))
      .catch((error: any) => {
        console.error("Failed to fetch body weight dates:", error);
        handleError(error);
      });
  }, [calendarMonth, idToken, handleError]);

  const handleLogBodyWeight = useCallback(async (data: { weight: string; fatPercentage: string; waistCircumference: string }): Promise<boolean> => {
    if (!selectedDate || !data.weight) {
      return false;
    }
    const dateKey = getDateKey(selectedDate);
    setBodyWeightLoading(true);
    setBodyWeightError(null);

    try {
      await BodyWeightService.logBodyWeight(dateKey, data);

      setBodyWeight(data.weight);
      setFatPercentage(data.fatPercentage);
      setWaistCircumference(data.waistCircumference);

      // Refresh body weight dates for the calendar
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      BodyWeightService.fetchBodyWeightDates(year, month)
        .then((dates) => setBodyWeightDays(new Set(dates)))
        .catch((error: any) => {
          console.error("Failed to refresh body weight dates:", error);
          handleError(error);
        });

      return true;
    } catch (error: any) {
      console.error("Error logging body weight:", error);
      setBodyWeightError(error.message || 'An unknown error occurred while logging body weight.');
      handleError(error);
      return false;
    } finally {
      setBodyWeightLoading(false);
    }
  }, [selectedDate, handleError]);

  const refreshBodyWeightForDate = useCallback(async (date: Date) => {
    const dateKey = getDateKey(date);
    try {
      const data = await BodyWeightService.fetchBodyWeightForDate(dateKey);
      setBodyWeight(data.weight);
      setFatPercentage(data.fatPercentage);
      setWaistCircumference(data.waistCircumference);
    } catch (error: any) {
      console.error("Failed to refresh bodyweight for date:", error);
      handleError(error);
    }
  }, [handleError]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    bodyWeight,
    setBodyWeight,
    fatPercentage,
    setFatPercentage,
    waistCircumference,
    setWaistCircumference,
    previousBodyWeight,
    bodyWeightDays,
    bodyWeightLoading,
    bodyWeightError,
    handleLogBodyWeight,
    refreshBodyWeightForDate,
  }), [
    bodyWeight,
    fatPercentage,
    waistCircumference,
    previousBodyWeight,
    bodyWeightDays,
    bodyWeightLoading,
    bodyWeightError,
    handleLogBodyWeight,
    refreshBodyWeightForDate,
  ]);

  return (
    <BodyWeightContext.Provider value={value}>
      {children}
    </BodyWeightContext.Provider>
  );
};

export const useBodyWeight = () => {
  const context = useContext(BodyWeightContext);
  if (context === undefined) {
    throw new Error('useBodyWeight must be used within BodyWeightProvider');
  }
  return context;
};
